import * as React from 'react';
import styled from 'styled-components';
import { colors, rawColors } from '../colors/color';
import { Flex } from '../compositeComponents/FlexHelpers';
import { ActionPlanRiskConsequence, ActionPlanRiskLikelihood } from '../../amorg-common';
import { riskAssessment } from '../../utils/riskAssessment';
import { Avatar } from '../avatar/Avatar';


type RiskAssessmentEvaluatorProps = {
    consequence: ActionPlanRiskConsequence,
    likelihood: ActionPlanRiskLikelihood,
    onChange: (consequence: ActionPlanRiskConsequence, likelihood: ActionPlanRiskLikelihood) => void;
}
export function RiskAssessmentEvaluator(props: RiskAssessmentEvaluatorProps):JSX.Element
{
    return (
        <RiskAssessmentEvaluatorWrapper>
            <RiskAssessmentEvaluatorSumWrapper consequence={props.consequence} likelihood={props.likelihood} />
            <ConsequenceWrapper consequence={props.consequence} onClick={(value) => props.onChange(value, props.likelihood)}></ConsequenceWrapper>
            <LikelihoodWrapper likelihood={props.likelihood} onClick={(value) => props.onChange(props.consequence, value)}></LikelihoodWrapper>
        </RiskAssessmentEvaluatorWrapper>
    );
}


type RiskAssessmentEvaluatorSumWrapperProps = {
    consequence:ActionPlanRiskConsequence;
    likelihood: ActionPlanRiskLikelihood;
}
function RiskAssessmentEvaluatorSumWrapper(props: RiskAssessmentEvaluatorSumWrapperProps): JSX.Element
{
    
    return (
        <RiskAssessmentEvaluatorSumWrapperStyled>
            <b>Risikovurdering</b>
            <Flex flexDirection="row" gap={8} alignItems="center">
                <Avatar size='24px' backgroundColor={colors.riskCategory(riskAssessment.calculateRisk(props.consequence, props.likelihood))}>
                    <span></span>
                </Avatar>
            <div>
                <span>{riskAssessment.calculateRisk(props.consequence, props.likelihood)}</span> 
            </div>
            </Flex>
        </RiskAssessmentEvaluatorSumWrapperStyled>
    );
}



type ConsequenceWrapperProps = {
    consequence: ActionPlanRiskConsequence;
    onClick: (value:ActionPlanRiskConsequence) => void;

}
function ConsequenceWrapper(props: ConsequenceWrapperProps): JSX.Element
{
    function handleConsequenceChange(value:ActionPlanRiskConsequence): void {
        props.onClick(value);
        console.log('Consequence changed to:', props.consequence);
    }
    
    return (
        <ConsequenceWrapperStyled>
            <SelectorWrapper>Konsekvens</SelectorWrapper>
            <SelectorTestWrapper>
                <ConsequenceSelector title={"Lille"} active={props.consequence === "1 - small"} onClick={() => handleConsequenceChange("1 - small")} />
                <ConsequenceSelector title={"Væsentlig"} active={props.consequence === "2 - significant"} onClick={() => handleConsequenceChange("2 - significant")} />
                <ConsequenceSelector title={"Alvorlig"} active={props.consequence === "3 - severe"} onClick={() => handleConsequenceChange("3 - severe")} />
                <ConsequenceSelector title={"Katastrofal"} active={props.consequence === "4 - catastrophic"} onClick={() => handleConsequenceChange("4 - catastrophic")} />
                <ConsequenceAnimationStyled position={props.consequence} />
            </SelectorTestWrapper>
        </ConsequenceWrapperStyled>
    );
}


type ConsequenceSelectorProps = {
    title: string,
    active: boolean,
    onClick: () => void
}

function ConsequenceSelector(props: ConsequenceSelectorProps):JSX.Element
{
    return (
        <SelectorStyled onClick={props.onClick} className={props.active ? 'active' : ''}>
            {props.title}
        </SelectorStyled>
    );
}


type LikelyhoodWrapperProps = {
    likelihood: ActionPlanRiskLikelihood;
    onClick: (value:ActionPlanRiskLikelihood) => void;
}
function LikelihoodWrapper(props: LikelyhoodWrapperProps): JSX.Element
{
    function handleFrequencyChange(value:ActionPlanRiskLikelihood): void {
        props.onClick(value);
    }

    return (
        <>
        <FrequencyWrapperStyled>
            <SelectorWrapper>Sandsynlighed</SelectorWrapper>
            <SelectorTestWrapper>
                <LikelyhoodSelector title={"Usandsynligt"} active={props.likelihood === '1 - unlikely'} onClick={() => handleFrequencyChange("1 - unlikely")} />
                <LikelyhoodSelector title={"Sjældent"} active={props.likelihood === '2 - rare'} onClick={() => handleFrequencyChange("2 - rare")} />
                <LikelyhoodSelector title={"Muligt"} active={props.likelihood === '3 - possible'} onClick={() => handleFrequencyChange("3 - possible")} />
                <LikelyhoodSelector title={"Sandsynligt"} active={props.likelihood === '4 - likely'} onClick={() => handleFrequencyChange("4 - likely")} />
                <LikelyhoodAnimationStyled position={props.likelihood} />
            </SelectorTestWrapper>
        </FrequencyWrapperStyled>
        </>
    );
}

type LikelihoodSelectorProps = {
    title?: string,
    active:boolean,
    onClick: () => void
}
function LikelyhoodSelector(props:LikelihoodSelectorProps): JSX.Element
{
    return (
        <SelectorStyled onClick={props.onClick} className={props.active ? 'active' : ''}>
            {props.title}
        </SelectorStyled>
    );
}


const RiskAssessmentEvaluatorSumWrapperStyled = styled.div`
    grid-area: evaluator;
    display: flex;
    flex-direction: column;
    align-items: start;
    gap:8px;
    font-size: 14px;
`;


const RiskAssessmentEvaluatorWrapper = styled.div`
    display: grid;
    grid-template-columns: 28% 70%;
    grid-template-rows: 1fr;
    grid-gap: 2%;

    grid-template-areas:
        "evaluator consequence"
        "evaluator frequency"
        
`;

const ConsequenceWrapperStyled = styled.div`
    align-items: center;
    grid-area: consequence;
    display: flex;
    flex-direction: row;
    gap: 4px;
`;

const FrequencyWrapperStyled = styled.div`
    align-items: center;
    grid-area: frequency;
    display: flex;
    flex-direction: row;
    gap: 4px;
`;


const SelectorTestWrapper = styled.div`
    border: 1px solid ${rawColors.gray.light};
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 4px;
    position: relative;
`;


const SelectorWrapper = styled.div`
    align-items: center;
    font-size: 14px;
    width: 160px;
`;


const ConsequenceAnimationStyled = styled.div<{ position: string }>`
    width: 25%;
    height: 25px;
    background: ${rawColors.gray.light};
    position: absolute;
    
    left: ${props => {
        switch (props.position) {
            case '1 - small':
                return '0%';
            case '2 - significant':
                return '25%';
            case '3 - severe':
                return '50%';
            case '4 - catastrophic':
                return '75%';
            default:
                return '0%';
        }
    }};


    display: flex;
    transition: left 0.5s ease-out;
    
`;


const LikelyhoodAnimationStyled = styled.div<{ position: string }>`
    width: 25%;
    height: 25px;
    background: ${rawColors.gray.light};
    position: absolute;

    left: ${props => {
        switch (props.position) {
            case '1 - unlikely':
                return '0%';
            case '2 - rare':
                return '25%';
            case '3 - possible':
                return '50%';
            case '4 - likely':
                return '75%';
            default:
                return '0%';
        }
    }};


    display: flex;
    transition: left 0.5s ease-out;

`;

const SelectorStyled = styled.div`
    /* background: #f0f0f0; */
    padding: 4px;
    /* border: 1px solid #ccc; */
    
    width: 25%;
    align-items: center;
    cursor: pointer;
    text-align: center;
    font-size: 12px;

    &.active {
        color:white;
        z-index: 1;
    }
`;

